import { provide, inject, computed } from 'vue';
import type { InjectionKey, Ref } from 'vue';

import { usePageData } from '~/composables/usePageData';
import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSponsoredItems from '~/composables/useSponsoredItems';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import useUpc from '~/modules/upc/composables/useUpc';
import { useProductType } from '~/composables/useProductType';

interface AdConfig {
  adId: string;
  pageType: string;
  deviceType: Ref<string>;
  brandName: Ref<string>;
  isUSA: boolean;
  isRegistry: boolean;
  envBrand: string;
  customStyleClass: Ref<string>;
  renderCondition: Ref<boolean>;
  canShowSponsored: boolean;
}

interface InternalSponsoredItemsConfig {
  categoryId: string;
  product: Product;
  pageType: string;
  showSponsoredItemTop: Ref<boolean>;
}

interface SiteMonConfig {
  clientId: string;
  device: 'desktop' | 'mobile';
  customStyleClass: string;
  isSimplificationOn: boolean;
  deviceType: 'DESKTOP' | 'MOBILE';
  debug: boolean;
  sponsoredItemsConfig: InternalSponsoredItemsConfig;
}

interface SponsoredItemsData {
  siteMonConfig: SiteMonConfig;
  isMonetizationEnabled: boolean;
  enableImpressionTagsForSMProducts: boolean;
  enableClickTagsForSMProducts: boolean;
  carouselOpenInNewTabEnabled: boolean;
  isBadgeSaleRed: boolean;
}

export interface SiteMonetizationData {
  showGAMAds: Ref<boolean>;
  showSponsoredItems: Ref<boolean>;
  placements: {
    top: AdConfig;
    inline: AdConfig;
    bottom: AdConfig;
  };
  sponsoredItems: SponsoredItemsData;
  selectedUpc: Ref<string>;
}

const SiteMonetizationKey: InjectionKey<SiteMonetizationData> = Symbol('SiteMonetization');

export function useSiteMonetizationProvide() {
  const { regionCode, shoppingMode } = usePageData();
  const { isMobile } = useDeviceType();
  const { showSponsoredItems, showGAMAds, shoppableRedesignOptimization } = useFeatureEligibility();
  const sponsoredItems = useSponsoredItems();
  const { brandName } = useCommonProductGetters();
  const { selectedUpcNumber } = useUpc();
  const { isGiftWithPurchase } = useProductType();
  const { isMcom } = useBrand();

  const activeBrand: 'mcom' | 'bcom' = isMcom ? 'mcom' : 'bcom';

  const topPlacement = { mcom: 'googleAdOnPdpTop', bcom: 'googleAdOnPdp' };
  const inlinePlacement = { mcom: 'googleAdOnPdp', bcom: 'googleAdOnPdpBottom' };

  const deviceType = computed(() => (isMobile.value ? 'mobile' : 'desktop'));
  const pageType = 'product';
  const isUSA = regionCode === 'US';
  const isRegistry = shoppingMode === 'WEDDING_REGISTRY';
  const spacingClasses = computed(() =>
    shoppableRedesignOptimization.value ? 'small-margin-bottom-m large-margin-bottom-xl' : 'small-margin-bottom-xl large-margin-bottom-xxl',
  );

  const commonAdConfig = {
    pageType,
    deviceType,
    brandName,
    isUSA,
    isRegistry,
    envBrand: activeBrand,
    customStyleClass: spacingClasses,
  };

  const monetizationData: SiteMonetizationData = {
    showGAMAds,
    showSponsoredItems,
    placements: {
      top: {
        ...commonAdConfig,
        adId: topPlacement[activeBrand],
        renderCondition: showGAMAds,
        canShowSponsored: false,
      },
      inline: {
        ...commonAdConfig,
        adId: inlinePlacement[activeBrand],
        renderCondition: showGAMAds,
        canShowSponsored: true,
      },
      bottom: {
        ...commonAdConfig,
        adId: 'googleAdOnPdpBottom',
        renderCondition: computed(() => showGAMAds.value && activeBrand !== 'bcom' && deviceType.value !== 'mobile' && !isGiftWithPurchase.value),
        canShowSponsored: false,
      },
    },
    sponsoredItems,
    selectedUpc: selectedUpcNumber,
  };

  provide(SiteMonetizationKey, monetizationData);
}

export function useSiteMonetization() {
  const data = inject(SiteMonetizationKey);
  if (!data) {
    throw new Error('`useSiteMonetization` is called outside of a `useSiteMonetizationProvide` context.');
  }
  return data;
}
